import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Header from '../../../components/ui/Header';


const InvioRicetta = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    farmacia: '',
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    indirizzo: '',
    citta: '',
    cap: '',
    noteConsegna: '',
    fasceOrarie: [],
    ricettaFiles: [], // Changed from ricettaFile to ricettaFiles array
    equivalenteOk: true,
    noteFarmaci: '',
    privacyAccettata: false
  });

  const [errors, setErrors] = useState({});

  // Pharmacy options
  const farmacie = [
    {
      id: 'filipponi',
      nome: 'Farmacia Dott.ssa A. Filipponi',
      dettagli: 'Centro Storico – Tel. +39 0733 230547',
      email: 'via.garibaldi@libero.it'
    },
    {
      id: 'petracci',
      nome: 'Farmacia Petracci',
      dettagli: 'Corso Cairoli – Tel. +39 0733 230412',
      email: 'info@farmaciapetracci.it'
    },
    {
      id: 'verdecaffe',
      nome: 'VerdeCaffe',
      dettagli: 'Sforzacosta – Tel. +39 0733 230291',
      email: 'verdecaffemacerata@gmail.com'
    }
  ];

  // Time slot options
  const fasceOrarie = [
    { id: 'mattina', label: 'Mattina (9–13)' },
    { id: 'pomeriggio', label: 'Pomeriggio (15–18)' },
    { id: 'sera', label: 'Sera (18–20)' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFarmaciaChange = (farmaciaId) => {
    setFormData(prev => ({ ...prev, farmacia: farmaciaId }));
    if (errors?.farmacia) {
      setErrors(prev => ({ ...prev, farmacia: '' }));
    }
  };

  const handleFasciaOrariaChange = (fasciaId) => {
    setFormData(prev => {
      const fasce = prev?.fasceOrarie?.includes(fasciaId)
        ? prev?.fasceOrarie?.filter(f => f !== fasciaId)
        : [...prev?.fasceOrarie, fasciaId];
      return { ...prev, fasceOrarie: fasce };
    });
    if (errors?.fasceOrarie) {
      setErrors(prev => ({ ...prev, fasceOrarie: '' }));
    }
  };

  const handleFileChange = (e, source) => {
    const files = Array.from(e?.target?.files || []);
    
    if (files?.length === 0) return;

    // Validate total number of files
    const currentFiles = formData?.ricettaFiles || [];
    const totalFiles = currentFiles?.length + files?.length;
    
    if (totalFiles > 5) {
      setErrors(prev => ({ ...prev, ricettaFiles: `Puoi caricare massimo 5 file. Hai già ${currentFiles?.length} file caricati.` }));
      return;
    }

    // Validate each file based on source
    const allowedTypes = source === 'camera' 
      ? ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif']
      : ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
    
    const maxSize = 5 * 1024 * 1024; // 5MB

    const validFiles = [];
    const errors = [];

    for (const file of files) {
      if (!allowedTypes?.includes(file?.type)) {
        errors?.push(`${file?.name}: Formato non valido`);
        continue;
      }
      if (file?.size > maxSize) {
        errors?.push(`${file?.name}: File troppo grande (max 5MB)`);
        continue;
      }
      validFiles?.push(file);
    }

    if (errors?.length > 0) {
      setErrors(prev => ({ ...prev, ricettaFiles: errors?.join(', ') }));
    } else {
      setErrors(prev => ({ ...prev, ricettaFiles: '' }));
    }

    if (validFiles?.length > 0) {
      setFormData(prev => ({ 
        ...prev, 
        ricettaFiles: [...(prev?.ricettaFiles || []), ...validFiles] 
      }));
    }

    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const handleRemoveFile = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      ricettaFiles: prev?.ricettaFiles?.filter((_, index) => index !== indexToRemove)
    }));
    if (errors?.ricettaFiles) {
      setErrors(prev => ({ ...prev, ricettaFiles: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Sezione 1 - Farmacia
    if (!formData?.farmacia) {
      newErrors.farmacia = 'Seleziona una sola farmacia';
    }

    // Sezione 2 - Dati cliente
    if (!formData?.nome?.trim()) {
      newErrors.nome = 'Il nome è obbligatorio';
    }
    if (!formData?.cognome?.trim()) {
      newErrors.cognome = 'Il cognome è obbligatorio';
    }
    if (!formData?.telefono?.trim()) {
      newErrors.telefono = 'Il telefono è obbligatorio';
    }

    // Sezione 3 - Consegna
    if (!formData?.indirizzo?.trim()) {
      newErrors.indirizzo = "L'indirizzo è obbligatorio";
    }
    if (!formData?.citta?.trim()) {
      newErrors.citta = 'La città è obbligatoria';
    }
    if (!formData?.cap?.trim()) {
      newErrors.cap = 'Il CAP è obbligatorio';
    }

    // Sezione 4 - Fascia oraria
    if (formData?.fasceOrarie?.length === 0) {
      newErrors.fasceOrarie = 'Seleziona almeno una fascia oraria';
    }

    // Sezione 5 - Ricetta
    if (!formData?.ricettaFiles || formData?.ricettaFiles?.length === 0) {
      newErrors.ricettaFiles = 'Carica almeno 1 file (JPG, PNG o PDF)';
    }

    // Sezione 6 - Privacy
    if (!formData?.privacyAccettata) {
      newErrors.privacyAccettata = 'Devi accettare il trattamento dei dati';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      setErrorMessage('Compila tutti i campi obbligatori');
      return;
    }

    setIsSubmitting(true);

    try {
      // Find selected pharmacy
      const farmaciaSelezionata = farmacie?.find(f => f?.id === formData?.farmacia);

      // Prepare multipart/form-data
      const formDataToSend = new FormData();
      formDataToSend?.append('to', farmaciaSelezionata?.email);
      formDataToSend?.append('subject', `Nuova richiesta consegna farmaci – ${formData?.nome} ${formData?.cognome}`);
      formDataToSend?.append('farmacia', farmaciaSelezionata?.nome);
      formDataToSend?.append('nome', formData?.nome);
      formDataToSend?.append('cognome', formData?.cognome);
      formDataToSend?.append('telefono', formData?.telefono);
      formDataToSend?.append('email', formData?.email || 'Non fornita');
      formDataToSend?.append('indirizzo', `${formData?.indirizzo}, ${formData?.citta} ${formData?.cap}`);
      formDataToSend?.append('noteConsegna', formData?.noteConsegna || 'Nessuna nota');
      formDataToSend?.append('fasceOrarie', formData?.fasceOrarie?.map(f => {
        const fascia = fasceOrarie?.find(fo => fo?.id === f);
        return fascia?.label;
      })?.join(', '));
      formDataToSend?.append('equivalenteOk', formData?.equivalenteOk ? 'Sì' : 'No');
      formDataToSend?.append('noteFarmaci', formData?.noteFarmaci || 'Nessuna nota');
      
      // Attach files directly
      if (formData?.ricettaFiles && formData?.ricettaFiles?.length > 0) {
        formData?.ricettaFiles?.forEach((file) => {
          formDataToSend?.append('files', file);
        });
      }

      // Get Supabase URL and anon key for edge function invocation
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      // Call Edge Function directly with multipart/form-data
      const response = await fetch(`${supabaseUrl}/functions/v1/send-pharmacy-prescription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: formDataToSend
      });

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result?.error || 'Errore durante l\'invio');
      }

      // Success
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/farma-service-city');
      }, 4000);

    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage(error?.message || 'Errore durante l\'invio. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-green-50 border border-green-200 rounded-xl shadow-lg p-12">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Icon name="Check" size={48} className="text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-green-600 mb-4">
              Richiesta inviata!
            </h2>
            <p className="text-gray-700 text-lg">
              La farmacia ti contatterà per conferma e tempi.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Shared Header Component */}
      <Header onFilterClick={() => {}} />

      {/* Hero Section */}
      <section className="w-full bg-gradient-to-b from-green-50 to-white py-12 md:py-16">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-green-600 mb-4">
            Invia Ricetta
          </h1>
          <p className="text-lg text-gray-700">
            Seleziona la farmacia, carica la ricetta e indica dove consegnare.
          </p>
        </div>
      </section>
      {/* Form Section */}
      <section className="w-full py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <form onSubmit={handleSubmit} className="space-y-10">
            {/* SEZIONE 1 - Scegli la Farmacia */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                1. Scegli la Farmacia
              </h2>
              <div className="space-y-4">
                {farmacie?.map((farmacia) => (
                  <div
                    key={farmacia?.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors"
                  >
                    <Checkbox
                      checked={formData?.farmacia === farmacia?.id}
                      onChange={() => handleFarmaciaChange(farmacia?.id)}
                      label={
                        <div>
                          <div className="font-semibold text-gray-900">{farmacia?.nome}</div>
                          <div className="text-sm text-gray-600">{farmacia?.dettagli}</div>
                        </div>
                      }
                    />
                  </div>
                ))}
              </div>
              {errors?.farmacia && (
                <p className="text-sm text-red-600 mt-2">{errors?.farmacia}</p>
              )}
            </div>

            {/* SEZIONE 2 - Dati Cliente */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                2. Dati Cliente
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Nome"
                    required
                    value={formData?.nome}
                    onChange={(e) => handleInputChange('nome', e?.target?.value)}
                    error={errors?.nome}
                    placeholder="Inserisci il tuo nome"
                  />
                  <Input
                    label="Cognome"
                    required
                    value={formData?.cognome}
                    onChange={(e) => handleInputChange('cognome', e?.target?.value)}
                    error={errors?.cognome}
                    placeholder="Inserisci il tuo cognome"
                  />
                </div>
                <Input
                  label="Telefono"
                  type="tel"
                  required
                  value={formData?.telefono}
                  onChange={(e) => handleInputChange('telefono', e?.target?.value)}
                  error={errors?.telefono}
                  placeholder="+39 123 456 7890"
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  placeholder="tua@email.it (opzionale)"
                />
              </div>
            </div>

            {/* SEZIONE 3 - Consegna */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                3. Dati Consegna
              </h2>
              <div className="space-y-4">
                <Input
                  label="Indirizzo completo"
                  required
                  value={formData?.indirizzo}
                  onChange={(e) => handleInputChange('indirizzo', e?.target?.value)}
                  error={errors?.indirizzo}
                  placeholder="Via, numero civico"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Città"
                    required
                    value={formData?.citta}
                    onChange={(e) => handleInputChange('citta', e?.target?.value)}
                    error={errors?.citta}
                    placeholder="Città"
                  />
                  <Input
                    label="CAP"
                    required
                    value={formData?.cap}
                    onChange={(e) => handleInputChange('cap', e?.target?.value)}
                    error={errors?.cap}
                    placeholder="62100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Note consegna
                  </label>
                  <textarea
                    value={formData?.noteConsegna}
                    onChange={(e) => handleInputChange('noteConsegna', e?.target?.value)}
                    placeholder="Eventuali indicazioni per la consegna..."
                    rows={3}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* SEZIONE 4 - Fascia Oraria */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                4. Fascia Oraria Preferita
              </h2>
              <div className="space-y-3">
                {fasceOrarie?.map((fascia) => (
                  <div
                    key={fascia?.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors"
                  >
                    <Checkbox
                      checked={formData?.fasceOrarie?.includes(fascia?.id)}
                      onChange={() => handleFasciaOrariaChange(fascia?.id)}
                      label={fascia?.label}
                    />
                  </div>
                ))}
              </div>
              {errors?.fasceOrarie && (
                <p className="text-sm text-red-600 mt-2">{errors?.fasceOrarie}</p>
              )}
            </div>

            {/* SEZIONE 5 - Ricetta */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                5. Carica Ricetta
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    File ricetta <span className="text-red-600">*</span>
                  </label>
                  
                  {/* Two separate upload buttons */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Button A: Scatta foto (Camera/Gallery) */}
                    <div>
                      <input
                        type="file"
                        id="camera-input"
                        accept="image/*"
                        capture="environment"
                        multiple
                        onChange={(e) => handleFileChange(e, 'camera')}
                        className="hidden"
                      />
                      <label
                        htmlFor="camera-input"
                        className="flex items-center justify-center space-x-2 w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg cursor-pointer transition-colors shadow-sm"
                      >
                        <Icon name="Camera" size={20} />
                        <span>Scatta foto</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        📸 Camera o galleria
                      </p>
                    </div>

                    {/* Button B: Carica da File (Files app) */}
                    <div>
                      <input
                        type="file"
                        id="file-input"
                        accept="image/*,application/pdf,.pdf"
                        multiple
                        onChange={(e) => handleFileChange(e, 'files')}
                        className="hidden"
                      />
                      <label
                        htmlFor="file-input"
                        className="flex items-center justify-center space-x-2 w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg cursor-pointer transition-colors shadow-sm"
                      >
                        <Icon name="FolderOpen" size={20} />
                        <span>Carica da File</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        📁 iCloud, Drive, PDF
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 mt-2">
                    Formati accettati: Immagini (JPG, PNG, WEBP, HEIC) o PDF • Max 5 file totali • Max 5MB per file
                  </p>
                  
                  {/* File List */}
                  {formData?.ricettaFiles && formData?.ricettaFiles?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-semibold text-green-700">
                        ✓ File selezionati ({formData?.ricettaFiles?.length}/5):
                      </p>
                      {formData?.ricettaFiles?.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <Icon 
                              name={file?.type === 'application/pdf' ? 'FileText' : 'Image'} 
                              size={24} 
                              className="text-green-600 flex-shrink-0" 
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {file?.name}
                              </p>
                              <p className="text-xs text-gray-600">
                                {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="ml-3 p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors flex-shrink-0"
                            aria-label="Rimuovi file"
                          >
                            <Icon name="X" size={20} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {errors?.ricettaFiles && (
                    <p className="text-sm text-red-600 mt-2 font-semibold">{errors?.ricettaFiles}</p>
                  )}
                </div>

                <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-900">
                    Equivalente ok?
                  </label>
                  <button
                    type="button"
                    onClick={() => handleInputChange('equivalenteOk', !formData?.equivalenteOk)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData?.equivalenteOk ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData?.equivalenteOk ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-sm font-semibold text-gray-700">
                    {formData?.equivalenteOk ? 'Sì' : 'No'}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Note farmaci
                  </label>
                  <textarea
                    value={formData?.noteFarmaci}
                    onChange={(e) => handleInputChange('noteFarmaci', e?.target?.value)}
                    placeholder="Eventuali indicazioni sui farmaci..."
                    rows={3}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* SEZIONE 6 - Privacy */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                6. Consenso Privacy
              </h2>
              <Checkbox
                checked={formData?.privacyAccettata}
                onChange={(e) => handleInputChange('privacyAccettata', e?.target?.checked)}
                required
                label="Autorizzo il trattamento dei dati (anche sanitari) esclusivamente per evadere la richiesta alla farmacia selezionata."
                error={errors?.privacyAccettata}
              />
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{errorMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-12 py-4 text-lg rounded-lg shadow-lg transition-all duration-200"
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia alla Farmacia'}
              </Button>
            </div>
          </form>

          {/* Footer Note */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500">
              I dati sanitari saranno trattati esclusivamente per l'evasione della richiesta.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvioRicetta;